import cx from "classnames";
import { useEffect, useState } from "react";

import { ProductSectionData } from "shared/product/ProductSection/product-section.helper";
import { Button } from "ui/Button";
import { StrapiImage } from "ui/StrapiImage";
import { useI18n } from "utils/with-i18n.utils";
type Tab = "description" | "technicals" | "features" | null;

type DetailsSectionProps = {
  productData: ProductSectionData;
};

export const DetailsSection = ({ productData }: DetailsSectionProps) => {
  const i18n = useI18n();
  const [selectedTab, setSelectedTab] = useState<Tab>(null);
  const [seeMore, setSeeMore] = useState(true);

  const modelName = `${productData.family.brand == "Apple" ? "l'" : "le "}${
    productData.family.modelName
  }`;

  // Onload, select the first tab and close seemore because before Google need to see all content
  useEffect(() => {
    setSelectedTab("description");
    setSeeMore(false);
  }, []);

  useEffect(() => {
    setSeeMore(false);
  }, [selectedTab]);

  if (!productData.productDetail) {
    return null;
  }

  return (
    <section className="bg-white">
      {/* Tab selector */}
      <TabSelector
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        modelName={modelName || ""}
      />

      {/* Content */}
      <TabContent selectedTab={selectedTab} tab="description">
        <h3 className="text-2xl font-medium text-center lg:text-4xl">
          {i18n.t("product.seo.description.title", { modelName })}
        </h3>
        <p className="text-2xl font-medium text-center lg:text-4xl">
          {productData.productDetail.description.subtitle}
        </p>

        <div className="flex flex-col justify-between items-center py-5 lg:flex-row">
          <div className="aspect-square relative ">
            <StrapiImage
              size="large"
              image={productData.productDetail.description.photo}
              className="w-96 h-96"
              loader={true}
              priority
              sizes="50vw"
            />
          </div>

          <p
            className="my-5 lg:w-1/2"
            dangerouslySetInnerHTML={{
              __html: productData.productDetail.description.content,
            }}
          />
        </div>

        {seeMore && (
          <div className="flex flex-col gap-10 lg:my-14">
            {productData.productDetail.description.details.map((feature) => (
              <div className="flex flex-col gap-3" key={feature.title}>
                <h4 className="text-xl font-bold lg:text-2xl">
                  {feature.title}
                </h4>
                {feature.items.map((item) => (
                  <div key={item.title}>
                    {item.title && (
                      <div className="flex gap-2.5 items-center ml-6">
                        <span className="block w-1.5 h-1.5 bg-cornflower-blue rounded-full" />
                        <h5 className="font-bold text-cornflower-blue lg:text-lg">
                          {item.title}
                        </h5>
                      </div>
                    )}
                    <p
                      className="ml-10 lg:text-lg"
                      dangerouslySetInnerHTML={{
                        __html: item.text,
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-center">
          <Button
            onClick={() => setSeeMore(!seeMore)}
            alternative="white"
            className="w-52"
          >
            {i18n.t(`product.seo.${seeMore ? "seeLess" : "seeMore"}`)}
          </Button>
        </div>
      </TabContent>

      <TabContent selectedTab={selectedTab} tab="technicals">
        <div className="flex flex-col gap-5 mb-14">
          {productData.productDetail.technical.sections
            .slice(0, seeMore ? undefined : 2)
            .map((section) => (
              <div className="flex flex-col" key={section.title}>
                <h4 className="mb-1 text-xl font-bold">{section.title}</h4>
                {section.items.map((item) => (
                  <div className="flex gap-3 py-2 border-b" key={item.name}>
                    <h5 className="w-1/2">{item.name}</h5>
                    <p className="flex-1">{item.value}</p>
                  </div>
                ))}
              </div>
            ))}
        </div>

        <div className="flex justify-center">
          <Button
            onClick={() => setSeeMore(!seeMore)}
            alternative="white"
            className="w-52"
          >
            {i18n.t(`product.seo.${seeMore ? "seeLess" : "seeMore"}`)}
          </Button>
        </div>
      </TabContent>

      <TabContent selectedTab={selectedTab} tab="features">
        <h3 className="text-2xl font-medium text-center lg:text-4xl">
          {i18n.t("product.seo.features.title", { modelName })}
        </h3>

        <div className="flex flex-col gap-10 my-14 lg:gap-14">
          {productData.productDetail.features
            ?.slice(0, seeMore ? undefined : 2)
            .map((feature) => (
              <div className="flex flex-col gap-3" key={feature.title}>
                <h4 className="text-xl font-bold lg:text-2xl">
                  {feature.title}
                </h4>
                {feature.items.map((item) => (
                  <div key={item.title}>
                    {item.title && (
                      <div className="flex gap-2.5 items-center ml-6">
                        <span className="block w-1.5 h-1.5 bg-cornflower-blue rounded-full" />
                        <h5 className="font-bold text-cornflower-blue lg:text-lg">
                          {item.title}
                        </h5>
                      </div>
                    )}
                    <p
                      className="ml-10 lg:text-lg"
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    />
                  </div>
                ))}
              </div>
            ))}
        </div>

        <div className="flex justify-center">
          <Button
            onClick={() => setSeeMore(!seeMore)}
            alternative="white"
            className="w-52"
          >
            {i18n.t(`product.seo.${seeMore ? "seeLess" : "seeMore"}`)}
          </Button>
        </div>
      </TabContent>
    </section>
  );
};

const TabSelector = ({
  selectedTab,
  setSelectedTab,
  modelName,
}: {
  selectedTab: Tab;
  setSelectedTab: (t: Tab) => void;
  modelName: string;
}) => {
  const i18n = useI18n();

  return (
    <div className="w-full bg-[#F9F7F2]">
      <div className="flex mx-auto w-full max-w-1440 text-sm lg:text-xl">
        {(["description", "technicals", "features"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setSelectedTab(t)}
            className={cx(
              "w-1/3 p-3.5 border-b-2 font-medium first-letter:uppercase",
              {
                "border-cornflower-blue text-black": selectedTab === t,
                "border-transparent text-black/40": selectedTab !== t,
              },
            )}
          >
            <p className="hidden lg:block">
              {i18n.t(`product.seo.${t}.tab`, { modelName })}
            </p>
            <p className="lg:hidden">
              {i18n.t(`product.seo.${t}.tabMobile`, { modelName })}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

const TabContent = ({
  selectedTab,
  tab,
  children,
}: {
  selectedTab: Tab;
  tab: Tab;
  children: React.ReactNode;
}) => (
  <article
    className={cx(
      "py-10 lg:py-14 px-5 lg:px-10 max-w-1110 mx-auto",
      [null, tab].includes(selectedTab) ? "block" : "hidden",
    )}
  >
    {children}
  </article>
);
